/**
 * QuickHASaleModal — single-form HA sale + fitting + invoice creator.
 *
 * Mounted from FittingLedgerPage. Calls POST /api/ha/quick-sale which
 * atomically writes ha_quick_sales + ha_fittings + invoices docs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import HASpecPicker from '../../components/HASpecPicker';
import PatientAdvancesBanner from '../billing/PatientAdvancesBanner';
import InlineApplyAdvancePanel, { preflightAdvance, allocateAdvance } from '../billing/InlineApplyAdvancePanel';

import useEscapeKey from '../../hooks/useEscapeKey';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HA_TYPES = ['BTE', 'RIC', 'ITE', 'ITC', 'CIC', 'IIC', 'POCKET', 'OTHER'];
const HA_TYPE_LABELS = { BTE:'BTE', RIC:'RIC', ITE:'ITE', ITC:'ITC', CIC:'CIC', IIC:'IIC', POCKET:'Pocket Aids', OTHER:'Other' };
const SIDES = [
  { value: 'both',  label: 'Both ears' },
  { value: 'left',  label: 'Left only' },
  { value: 'right', label: 'Right only' },
];
const PAY_MODES = [
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'card',          label: 'Card' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cheque',        label: 'Cheque' },
];
const COMMON_BRANDS = ['Phonak', 'ReSound', 'Oticon', 'Signia', 'Widex', 'Starkey', 'Unitron'];

const todayISO = () => new Date().toISOString().slice(0, 10);

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[10px] text-slate-400 mt-0.5">{hint}</span>}
    </label>
  );
}

export default function QuickHASaleModal({
  onClose, onCreated, prefillPatientId,
  // Additional prefills for the "Convert Trial → Sale" flow —
  // audiologist has already picked brand+model during the trial,
  // so we pre-fill them and leave the fresh saleable-stock serials
  // blank for the audiologist to enter.
  prefillBrand, prefillModel, prefillHaType,
}) {
  const [branches, setBranches] = useState([]);
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // 2026-02-13 · HA product catalogue for the Sell-as chip toggle.
  // Loaded once on mount so we can resolve the picked brand+model →
  // `sellable_as_single` / `sellable_as_kit` flags and show/hide the
  // toggle accordingly. Walk-in / external Quick Sales that don't
  // match a catalogue SKU fall through to today's dropdown behaviour.
  const [catalogueProducts, setCatalogueProducts] = useState([]);

  const [form, setForm] = useState({
    branch_id: '',
    brand: prefillBrand || '',
    model: prefillModel || '',
    ha_type: prefillHaType || 'BTE',
    // sale_unit tells the backend whether both-ears means one kit-line
    // (sale_unit ∈ {'pair','kit'} → qty=1) or two single-line units
    // (sale_unit='single' → qty=2). Auto-populated when the audiologist
    // picks a serial from the inventory picker; otherwise defaults to
    // 'single' — the sensible default for legacy SKUs with no catalogue
    // packing. Fixes the /ha/analytics/revenue double-count that
    // previously reported kit-priced sales at 2× their real value.
    sale_unit: 'single',
    serial_left: '', serial_right: '',
    serial_left_cost: '', serial_right_cost: '',
    serial_left_grn: '', serial_right_grn: '',
    side: 'both',
    fitting_date: todayISO(),
    warranty_months: 12,
    extended_warranty: false,
    extended_warranty_months: '',
    extended_warranty_source: 'manufacturer',
    mrp: '', sale_price: '',
    discount_amount: '',
    gst_rate: 18,
    payment_status: 'fully_paid',
    payment_mode: 'cash',
    payment_date: todayISO(),
    advance_amount: '',
    expected_payment_date: '',
    notes: '',
  });
  // Device spec — colour + power + wire/tube length. Shape depends on
  // form.side: for 'left'/'right' it's a flat object; for 'both' it's
  // { left: {...}, right: {...} } so audiologist can capture different
  // wire lengths & powers per ear (asymmetric losses are common).
  const [spec, setSpec] = useState({});
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ub = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  // Live inventory lookup state per side: {status, reason, state}
  // status: 'available' | 'conflict' | 'not_found' | 'checking' | null
  const [serialState, setSerialState] = useState({ left: null, right: null });

  // ── Unsaved-changes guard (2026-08-27) ─────────────────────────
  // The form is "dirty" as soon as the audiologist has picked a
  // patient OR typed anything meaningful. `guardedClose` intercepts
  // Cancel / × / Escape: first attempt shows an amber pill, second
  // attempt within 5 s actually closes. Prevents accidental discard
  // of half-entered sales.
  const isDirty = !!(
    picked ||
    form.serial_left ||
    form.serial_right ||
    form.serial_left_cost ||
    form.serial_right_cost ||
    form.serial_left_grn ||
    form.serial_right_grn ||
    form.mrp ||
    form.sale_price ||
    form.notes ||
    form.advance_amount ||
    form.brand !== (prefillBrand || '') ||
    form.model !== (prefillModel || '')
  );
  const { guardedClose, pill: unsavedPill } = useUnsavedChangesGuard(isDirty, onClose);
  useEscapeKey(guardedClose);

  // Phase 2B.3 · Inline Apply-Advance state. `enabled` toggles the panel;
  // `receiptId` + `amount` are captured from the picker. On submit we
  // (1) pre-flight the advance, (2) create the sale, (3) POST allocation.
  const [applyAdv, setApplyAdv] = useState({ enabled: false, receiptId: null, amount: '' });
  const [advWarning, setAdvWarning] = useState('');

  // When the audiologist toggles Side (both ↔ left ↔ right) the spec
  // shape must follow — flat for single-ear, {left, right} for both.
  // Preserve any captured spec by mapping across the transition
  // instead of resetting silently.
  useEffect(() => {
    setSpec((prev) => {
      const isBothShape = prev && (prev.left || prev.right);
      if (form.side === 'both' && !isBothShape) {
        // Flat → both — mirror onto both ears (audiologist can adjust).
        return { left: prev || {}, right: prev || {} };
      }
      if ((form.side === 'left' || form.side === 'right') && isBothShape) {
        // Both → single — keep the matching ear.
        return prev[form.side] || {};
      }
      return prev || {};
    });
  }, [form.side]);

  // Load branches + HA catalogue + (optional) prefill patient
  useEffect(() => {
    (async () => {
      try {
        const b = await axios.get(`${API}/branches`);
        setBranches(b.data);
        if (b.data[0]) setForm((f) => ({ ...f, branch_id: b.data[0].branch_id }));
      } catch { /* ignore */ }
      try {
        const cp = await axios.get(`${API}/ha/products`);
        setCatalogueProducts(Array.isArray(cp.data) ? cp.data : []);
      } catch { /* ignore — Sell-as toggle just stays hidden */ }
      if (prefillPatientId) {
        try {
          const p = await axios.get(`${API}/patients/${prefillPatientId}`);
          setPicked(p.data);
        } catch { /* ignore */ }
      }
    })();
  }, [prefillPatientId]);

  // Resolve the picked catalogue SKU from the typed / picked brand +
  // model + form-factor. Case-insensitive since audiologists sometimes
  // type "phonak" but the catalogue row is "Phonak".
  const pickedCatalogueSKU = useMemo(() => {
    if (!form.brand || !form.model) return null;
    const b = form.brand.trim().toLowerCase();
    const m = form.model.trim().toLowerCase();
    return catalogueProducts.find((p) =>
      (p.brand || '').toLowerCase() === b
      && (p.model || '').toLowerCase() === m
    ) || null;
  }, [catalogueProducts, form.brand, form.model]);

  // Sell-as toggle gate. When the SKU allows both packing modes we let
  // the audiologist pick between Single Unit and Kit for THIS sale.
  // Kit-only or Single-only SKUs auto-lock. Unknown SKUs (walk-in
  // Quick Sales with no catalogue match) fall back to today's dropdown.
  const skuAllowsSingle = pickedCatalogueSKU
    ? (pickedCatalogueSKU.sellable_as_single !== false)
    : null;
  const skuAllowsKit = pickedCatalogueSKU
    ? !!pickedCatalogueSKU.sellable_as_kit
    : null;
  const showSellAsToggle =
    pickedCatalogueSKU !== null
    && (skuAllowsSingle || skuAllowsKit);

  // Auto-lock `form.sale_unit` when the picked SKU only allows one
  // packing mode. Prevents accidental single-priced billing on a
  // kit-only SKU (or vice-versa).
  useEffect(() => {
    if (!pickedCatalogueSKU) return;
    if (skuAllowsKit && !skuAllowsSingle && form.sale_unit !== 'kit') {
      setForm((f) => ({ ...f, sale_unit: 'kit' }));
    } else if (skuAllowsSingle && !skuAllowsKit
               && (form.sale_unit === 'kit' || form.sale_unit === 'pair')) {
      setForm((f) => ({ ...f, sale_unit: 'single' }));
    }
    // Both allowed → leave `form.sale_unit` alone; the audiologist picks.
  }, [pickedCatalogueSKU, skuAllowsKit, skuAllowsSingle]); // eslint-disable-line

  // Patient search (debounced)
  useEffect(() => {
    if (!search || search.length < 2) { setPatients([]); return; }
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/patients`, { params: { search, limit: 10 } });
        setPatients(Array.isArray(r.data) ? r.data : []);
      } catch { setPatients([]); }
    }, 200);
    return () => clearTimeout(h);
  }, [search]);

  // Live serial lookup (debounced) — only for sides that are actually visible.
  useEffect(() => {
    const sides = form.side === 'both' ? ['left', 'right'] : [form.side];
    const handles = [];
    sides.forEach((s) => {
      const v = (s === 'left' ? form.serial_left : form.serial_right).trim();
      if (v.length < 3) {
        setSerialState((p) => ({ ...p, [s]: null }));
        return;
      }
      setSerialState((p) => ({ ...p, [s]: { status: 'checking' } }));
      const h = setTimeout(async () => {
        try {
          const r = await axios.get(`${API}/ha/serials/lookup`, { params: { serial_no: v, branch_id: form.branch_id || undefined } });
          setSerialState((p) => ({ ...p, [s]: r.data }));
        } catch (e) {
          setSerialState((p) => ({ ...p, [s]: { status: 'error', reason: e?.message } }));
        }
      }, 350);
      handles.push(h);
    });
    return () => handles.forEach(clearTimeout);
  }, [form.serial_left, form.serial_right, form.side, form.branch_id]);

  // ── Inventory picker (2026-08-28) ────────────────────────────────
  // Bug-fix: previously this modal only allowed manual serial entry, so
  // audiologists opening "+ New Fitting" (which lands here — 95% of
  // fittings happen at sale) had no visibility into saleable IN_STOCK.
  // The sister "New Fitting Session" flow already used the exact same
  // `/ha/fittings/available-stock` endpoint. Wiring it here closes the
  // inconsistency and lets counter staff pick a serial from stock
  // instead of copy-typing it (which was also error-prone). Manual
  // entry is preserved for external / consignment serials (see the
  // amber "External-sourced" advisory further down).
  const [pickResults, setPickResults] = useState({ models: [], total_available: 0 });
  const [pickLoading, setPickLoading] = useState(false);

  useEffect(() => {
    if (!form.branch_id) {
      setPickResults({ models: [], total_available: 0 });
      return;
    }
    let cancelled = false;
    setPickLoading(true);
    const h = setTimeout(async () => {
      try {
        const params = { branch_id: form.branch_id };
        if (form.brand) params.brand = form.brand;
        if (form.model) params.model = form.model;
        if (form.ha_type) params.form_factor = form.ha_type;
        const r = await axios.get(`${API}/ha/fittings/available-stock`, { params });
        if (!cancelled) setPickResults(r.data || { models: [], total_available: 0 });
      } catch {
        if (!cancelled) setPickResults({ models: [], total_available: 0 });
      } finally {
        if (!cancelled) setPickLoading(false);
      }
    }, 220);
    return () => { cancelled = true; clearTimeout(h); };
  }, [form.branch_id, form.brand, form.model, form.ha_type]);

  // Click a serial in the inventory picker →
  //   * Toggle-off if it's already picked on either ear (2nd click clears).
  //   * Otherwise auto-fill brand/model on first pick and assign to the
  //     matching ear (side='left'/'right' fills that ear; side='both'
  //     fills left first then right).
  // Preserves the existing debounced-lookup logic — the auto-fill flows
  // through the same `form.serial_left`/`serial_right` state that the
  // debounce effect above watches, so the "available" chip lights up
  // for free.
  const pickInventoryUnit = (unit, model) => {
    const sn = (unit.serial_no || '').toUpperCase();
    setForm((f) => {
      const currLeft  = (f.serial_left  || '').toUpperCase();
      const currRight = (f.serial_right || '').toUpperCase();
      if (sn === currLeft)  return { ...f, serial_left:  '' };
      if (sn === currRight) return { ...f, serial_right: '' };
      const patch = { ...f };
      if (!f.brand) patch.brand = model.brand;
      if (!f.model) patch.model = model.model;
      // 2026-02-11 fix — auto-populate sale_unit from the catalogue
      // SKU that owns this serial so the backend billing-line qty is
      // computed correctly (kit → qty=1 for both ears, single → qty=2).
      // We only overwrite if the user hasn't manually picked a
      // different sale_unit (they can still change it after the pick).
      if (model.sale_unit && f.sale_unit === 'single' && !f.brand) {
        patch.sale_unit = model.sale_unit;
      }
      // Prefill MRP from the catalogue on the first pick so the
      // audiologist doesn't have to re-type it — but only when MRP is
      // blank (respecting any value they may have already typed).
      if (typeof model.mrp === 'number' && model.mrp > 0 && !f.mrp) {
        patch.mrp = model.mrp;
      }
      if (f.side === 'left') {
        patch.serial_left = unit.serial_no;
      } else if (f.side === 'right') {
        patch.serial_right = unit.serial_no;
      } else {
        // 'both' — fill left first, then right on the next click.
        if (!f.serial_left) patch.serial_left = unit.serial_no;
        else                patch.serial_right = unit.serial_no;
      }
      return patch;
    });
  };

  // Auto-calc discount = MRP - sale_price (only if user hasn't manually overridden)
  const discountAuto = useMemo(() => {
    const m = parseFloat(form.mrp || 0);
    const s = parseFloat(form.sale_price || 0);
    if (m > 0 && s >= 0 && m >= s) return Math.max(0, m - s).toFixed(2);
    return '';
  }, [form.mrp, form.sale_price]);

  const balance = useMemo(() => {
    const total = parseFloat(form.sale_price || 0);
    if (form.payment_status === 'fully_paid') return 0;
    if (form.payment_status === 'unpaid') return total;
    return Math.max(0, total - parseFloat(form.advance_amount || 0));
  }, [form.sale_price, form.payment_status, form.advance_amount]);

  const submit = async () => {
    setErr('');
    if (!picked) { setErr('Please pick a patient.'); return; }
    if (!form.branch_id) { setErr('Branch is required.'); return; }
    if (!form.brand.trim()) { setErr('Brand (Make) is required.'); return; }
    if (!form.model.trim()) { setErr('Model is required.'); return; }

    // Side-aware serial validation
    const sLeft = form.serial_left.trim();
    const sRight = form.serial_right.trim();
    if (form.side === 'both') {
      if (!sLeft || !sRight) { setErr('Both ears requires Left AND Right serial numbers.'); return; }
      if ((sLeft || '').toUpperCase() === (sRight || '').toUpperCase()) { setErr('Left and Right serial numbers must be different.'); return; }
    } else if (form.side === 'left' && !sLeft) {
      setErr('Left serial number is required.'); return;
    } else if (form.side === 'right' && !sRight) {
      setErr('Right serial number is required.'); return;
    }
    // Block submit if any visible serial is in 'conflict' state (already SOLD)
    const sidesToCheck = form.side === 'both' ? ['left', 'right'] : [form.side];
    for (const s of sidesToCheck) {
      const st = serialState[s];
      if (st?.status === 'conflict') {
        setErr(`${(s.charAt(0) || '').toUpperCase() + s.slice(1)} serial is unavailable: ${st.reason || st.state}`);
        return;
      }
    }
    const mrp = parseFloat(form.mrp);
    const sale = parseFloat(form.sale_price);
    if (!Number.isFinite(mrp) || mrp < 0) { setErr('Enter a valid MRP.'); return; }
    if (!Number.isFinite(sale) || sale < 0) { setErr('Enter a valid sale price.'); return; }
    if (sale > mrp + 0.5) { setErr('Sale price cannot exceed MRP.'); return; }
    if (form.payment_status === 'advance_paid') {
      const adv = parseFloat(form.advance_amount);
      if (!Number.isFinite(adv) || adv <= 0) { setErr('Advance amount required when "Advance paid".'); return; }
      if (adv > sale + 0.5) { setErr('Advance cannot exceed sale price.'); return; }
    }

    // Phase 2B.3 · Inline Apply-Advance validation
    let advToApply = null;
    if (applyAdv.enabled) {
      if (!applyAdv.receiptId) { setErr('Pick an advance receipt to apply.'); return; }
      const amt = Number(applyAdv.amount);
      if (!Number.isFinite(amt) || amt <= 0) { setErr('Apply-Advance amount must be > 0.'); return; }
      // Combined ceiling: initial cash payment + advance ≤ sale price.
      // For `fully_paid` the effective cash is (sale − advance), NOT the
      // full sale (2026-08-26 fix). Before this, staff selecting an
      // advance while status="Fully paid" tripped an inline "Advance +
      // cash exceeds sale price" error because the panel double-counted
      // the sale as both fully-paid cash AND advance. The advance is a
      // separate payment line, so cash-now = sale − advance.
      const cashPortion = form.payment_status === 'fully_paid'
        ? Math.max(0, sale - amt)
        : (form.payment_status === 'advance_paid' ? Number(form.advance_amount || 0) : 0);
      if (amt + cashPortion > sale + 0.5) {
        setErr(`Advance (₹${amt}) + cash payment (₹${cashPortion}) exceeds sale price (₹${sale}). Reduce one.`);
        return;
      }
      advToApply = { receiptId: applyAdv.receiptId, amount: amt };
    }
    setSaving(true);
    setAdvWarning('');
    try {
      // Pre-flight: re-fetch the selected receipt right before writing.
      // Fails fast if a concurrent user consumed the balance. No invoice
      // is created until this passes.
      if (advToApply) {
        try {
          await preflightAdvance(advToApply);
        } catch (pf) {
          setErr(pf.message || 'Advance pre-flight failed');
          setSaving(false);
          return;
        }
      }
      // ── Advance-aware payment remap (2026-08-26) ──────────────────
      // When Apply-Advance is active AND status="Fully paid", the
      // backend must NOT be told to record ₹sale as cash — because the
      // Advance Receipt allocation immediately after would over-pay the
      // invoice. Remap:
      //   • advance ≥ sale  → payment_status="unpaid" (no cash payment
      //     row; allocation closes the invoice from draft → paid).
      //   • 0 < advance < sale → payment_status="advance_paid" with
      //     advance_amount = sale − advance (this is the cash-now).
      //     After allocation runs, `record_payment_atomic` recomputes
      //     the invoice status to "paid" because balance hits zero.
      // `advance_amount` on this endpoint semantically means "cash
      // paid at the counter now" — NOT to be confused with the AR
      // being consumed via `advToApply` (a separate payment line).
      let effectiveStatus = form.payment_status;
      let effectiveAdvanceAmt = form.payment_status === 'advance_paid'
        ? parseFloat(form.advance_amount) : null;
      if (advToApply && form.payment_status === 'fully_paid') {
        const cashNow = Math.max(0, +(sale - advToApply.amount).toFixed(2));
        if (cashNow <= 0.005) {
          effectiveStatus = 'unpaid';
          effectiveAdvanceAmt = null;
        } else {
          effectiveStatus = 'advance_paid';
          effectiveAdvanceAmt = cashNow;
        }
      }
      const body = {
        patient_id: picked.patient_id,
        branch_id: form.branch_id,
        brand: form.brand.trim(),
        model: form.model.trim(),
        ha_type: form.ha_type,
        serial_left: form.side === 'right' ? null : (sLeft || null),
        serial_right: form.side === 'left' ? null : (sRight || null),
        // Optional cost prices for externally-sourced serials — only sent
        // when a serial is flagged `not_found` and the audiologist typed
        // a cost. The backend auto-creates the SOLD `serial_items` row
        // and stamps this as `serial_items.unit_cost` for margin reports.
        serial_left_cost: (form.side !== 'right' && serialState.left?.status === 'not_found'
          && form.serial_left_cost !== '' && Number.isFinite(parseFloat(form.serial_left_cost)))
          ? parseFloat(form.serial_left_cost) : null,
        serial_right_cost: (form.side !== 'left' && serialState.right?.status === 'not_found'
          && form.serial_right_cost !== '' && Number.isFinite(parseFloat(form.serial_right_cost)))
          ? parseFloat(form.serial_right_cost) : null,
        // GRN numbers (Goods Receipt Note) — only sent when the serial
        // is `not_found` and the audiologist typed a value. Stamped on
        // both `serial_items.grn_no` and `ha_fittings.serial_*_grn`.
        serial_left_grn: (form.side !== 'right' && serialState.left?.status === 'not_found'
          && form.serial_left_grn && form.serial_left_grn.trim())
          ? form.serial_left_grn.trim() : null,
        serial_right_grn: (form.side !== 'left' && serialState.right?.status === 'not_found'
          && form.serial_right_grn && form.serial_right_grn.trim())
          ? form.serial_right_grn.trim() : null,
        side: form.side,
        fitting_date: form.fitting_date,
        warranty_months: parseInt(form.warranty_months) || 12,
        extended_warranty: !!form.extended_warranty,
        extended_warranty_months: form.extended_warranty && form.extended_warranty_months
          ? parseInt(form.extended_warranty_months) : null,
        extended_warranty_source: form.extended_warranty ? form.extended_warranty_source : null,
        mrp, sale_price: sale,
        sale_unit: form.sale_unit || 'single',
        discount_amount: form.discount_amount !== '' ? parseFloat(form.discount_amount) : null,
        gst_rate: parseFloat(form.gst_rate) || 0,
        payment_status: effectiveStatus,
        payment_mode: effectiveStatus === 'unpaid' ? null : (form.payment_mode || null),
        payment_date: effectiveStatus === 'unpaid' ? null : (form.payment_date || null),
        advance_amount: effectiveAdvanceAmt,
        expected_payment_date: form.expected_payment_date || null,
        notes: form.notes || null,
        // Device spec (colour + power + length). Backend accepts the
        // whole blob and forwards to inventory / fitting docs so the
        // stock unit knows what wire/tube shipped.
        spec: spec && Object.keys(spec).length ? spec : null,
      };
      const r = await axios.post(`${API}/ha/quick-sale`, body);
      // Phase 2B.3 · Post-allocation. The sale endpoint returned the
      // freshly-created invoice_id. We call the authoritative Phase 2B.2
      // writer to record the advance as a payment on that invoice.
      // On failure: the invoice EXISTS unmodified (grand_total preserved,
      // no phantom advance payment). We surface a clear warning so staff
      // can apply the advance manually via the Advance Receipts screen.
      if (advToApply && r.data?.invoice_id) {
        try {
          await allocateAdvance({
            receiptId: advToApply.receiptId,
            invoiceId: r.data.invoice_id,
            amount: advToApply.amount,
          });
        } catch (allocErr) {
          const detail = allocErr?.response?.data?.detail || allocErr?.message || 'Advance allocation failed';
          setAdvWarning(
            `Sale ${r.data.sale_no || r.data.invoice_no || ''} was created, but the advance could not be applied: ${detail}. ` +
            `The invoice remains open at its full balance. Open Advance Receipts → click "Apply Advance" on the new invoice to retry.`,
          );
          // Deliberately still call onCreated — the sale itself succeeded.
        }
      }
      onCreated && onCreated(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (e?.message || 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
        
         data-testid="quick-ha-sale-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold">Add Hearing Aid Sale</h2>
            <p className="text-[11px] opacity-90">Records a fitting, sale and invoice in one shot.</p>
          </div>
          <button onClick={guardedClose} className="text-white/90 hover:text-white text-2xl leading-none" aria-label="Close" data-testid="quick-ha-close">×</button>
        </div>

        <div className="p-5 space-y-5">
          {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded px-3 py-2" data-testid="quick-ha-err">{err}</div>}
          {advWarning && (
            <div className="bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded px-3 py-2" data-testid="qha-apply-advance-warning">
              {advWarning}
            </div>
          )}

          {/* Patient + Branch */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Patient & Branch</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Patient" required>
                {picked ? (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 text-sm" data-testid="quick-ha-patient">
                    <span className="flex-1 font-semibold">{picked.name}</span>
                    <span className="text-[11px] text-slate-500">{picked.mrd_no || ''}</span>
                    <button onClick={() => setPicked(null)} className="text-rose-500 text-xs hover:underline">✕</button>
                  </div>
                ) : (
                  <>
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / mobile / MRD…"
                      data-testid="quick-ha-patient-search"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-indigo-500" />
                    {patients.length > 0 && (
                      <div className="mt-1 max-h-40 overflow-auto border border-slate-200 rounded">
                        {patients.map((p) => (
                          <button key={p.patient_id} onClick={() => setPicked(p)}
                            data-testid={`quick-ha-patient-pick-${p.patient_id}`}
                            className="block w-full text-left text-xs px-2 py-1 hover:bg-indigo-50">
                            <span className="font-semibold">{p.name}</span>{' '}
                            <span className="text-slate-500">({p.mobile || '—'} · {p.mrd_no || '—'})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </Field>

              <Field label="Branch" required>
                <select value={form.branch_id} onChange={u('branch_id')}
                  data-testid="quick-ha-branch"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                  {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{b.name}</option>)}
                </select>
              </Field>
            </div>
            {/* Phase 2B.3 · alert staff if this patient has an existing
                unused Advance Receipt. Informational only — clicking
                opens the Advance Receipts screen where staff apply
                the advance after this sale creates its invoice. */}
            {picked?.patient_id && (
              <div className="mt-3">
                <PatientAdvancesBanner patientId={picked.patient_id} />
              </div>
            )}
            {/* Phase 2B.3 (UX Correction) · Inline Apply-Advance panel.
                Renders inside the sale form so staff can wire the
                advance in one shot — no navigation. Fails fast if the
                advance balance drifted during entry (pre-flight),
                otherwise applies AFTER the sale invoice exists via the
                authoritative Phase 2B.2 writer. */}
            {picked?.patient_id && (
              <div className="mt-3">
                <InlineApplyAdvancePanel
                  patientId={picked.patient_id}
                  salePrice={parseFloat(form.sale_price) || 0}
                  value={applyAdv}
                  onChange={setApplyAdv}
                  disabled={saving}
                  testidPrefix="qha-apply-advance"
                />
              </div>
            )}
          </section>

          {/* Hearing aid */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Hearing Aid</h3>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Make / Brand" required>
                <input list="qha-brands" value={form.brand} onChange={u('brand')} data-testid="quick-ha-brand"
                  placeholder="e.g. Phonak"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
                <datalist id="qha-brands">
                  {COMMON_BRANDS.map((b) => <option key={b} value={b} />)}
                </datalist>
              </Field>
              <Field label="Model" required>
                <input value={form.model} onChange={u('model')} data-testid="quick-ha-model"
                  placeholder="e.g. Audeo Paradise P50"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
              </Field>
              <Field label="Type">
                <select value={form.ha_type} onChange={u('ha_type')} data-testid="quick-ha-type"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                  {HA_TYPES.map((t) => <option key={t} value={t}>{HA_TYPE_LABELS[t] || t}</option>)}
                </select>
              </Field>
              <Field label="Sell as" hint={
                showSellAsToggle
                  ? (form.sale_unit === 'kit' || form.sale_unit === 'pair'
                      ? 'Kit — bills ONE line at the kit price (covers both ears).'
                      : 'Single — bills TWO line items when Side = Both ears.')
                  : (form.sale_unit === 'kit' || form.sale_unit === 'pair'
                      ? 'Kit price covers both ears — the invoice will bill 1 line at the kit price.'
                      : 'Each ear priced separately — the invoice will bill 2 line items when Side = Both ears.')
              }>
                {showSellAsToggle ? (
                  // 2026-02-13 · Chip toggle — auto-locks when SKU is
                  // single-only or kit-only (see useEffect above);
                  // otherwise both chips are clickable so the
                  // audiologist can pick per-sale.
                  <div
                    className="grid grid-cols-2 gap-1.5"
                    role="radiogroup"
                    data-testid="quick-ha-sell-as-toggle"
                  >
                    {[
                      { k: 'single', lab: 'Single', hint: '1 aid · priced per unit', allowed: skuAllowsSingle },
                      { k: 'kit',    lab: 'Kit',    hint: 'R+L pair · one price',     allowed: skuAllowsKit },
                    ].map(({ k, lab, hint, allowed }) => {
                      const isKit = (k === 'kit');
                      const active =
                        isKit
                          ? (form.sale_unit === 'kit' || form.sale_unit === 'pair')
                          : (form.sale_unit === 'single');
                      const cls = !allowed
                        ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
                        : active
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50';
                      return (
                        <button
                          key={k}
                          type="button"
                          disabled={!allowed}
                          onClick={() => allowed && setForm((f) => ({ ...f, sale_unit: k }))}
                          data-testid={`quick-ha-sell-as-${k}`}
                          title={allowed ? hint : `This SKU is not sellable as ${lab}`}
                          className={`text-left px-2 py-1.5 border rounded transition ${cls}`}
                        >
                          <div className="text-[11px] font-semibold">{lab}</div>
                          <div className={`text-[9px] leading-tight ${active && allowed ? 'opacity-90' : 'opacity-70'}`}>{hint}</div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <select
                    value={form.sale_unit || 'single'}
                    onChange={u('sale_unit')}
                    data-testid="quick-ha-sale-unit"
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  >
                    <option value="single">1 · Single (one aid)</option>
                    <option value="pair">2 · Pair (two aids)</option>
                    <option value="kit">Kit (boxed pair)</option>
                  </select>
                )}
              </Field>
              <Field label="Side fitted">
                <select value={form.side} onChange={u('side')} data-testid="quick-ha-side"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                  {SIDES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Fitting date" required>
                <input type="date" value={form.fitting_date} onChange={u('fitting_date')}
                  data-testid="quick-ha-fitting-date"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
              </Field>
            </div>

            {/* ── Inventory picker (2026-08-28) ───────────────────
                Renders whenever a branch is selected. Fetches
                saleable IN_STOCK from /ha/fittings/available-stock and
                lets audiologists click a serial to auto-fill the
                matching-ear input. Manual serial entry (below) is
                preserved for external/consignment stock.
                Sister flow: NewFittingModal in FittingLedgerPage uses
                the exact same endpoint — this restores parity. */}
            {form.branch_id && (
              <div
                className="mt-3 border border-indigo-200 bg-indigo-50/40 rounded p-3"
                data-testid="quick-ha-inventory-picker"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-800">
                    Pick from available inventory
                  </span>
                  <span
                    className="text-[10px] text-slate-500"
                    data-testid="quick-ha-picker-count"
                  >
                    {pickResults.total_available} available at this branch
                  </span>
                </div>
                {pickLoading ? (
                  <div className="text-[11px] italic text-slate-400 py-2">Loading inventory…</div>
                ) : pickResults.models.length === 0 ? (
                  <div
                    className="text-[11px] italic text-slate-500 py-2"
                    data-testid="quick-ha-picker-empty"
                  >
                    No saleable stock at this branch matching {form.brand || 'any brand'} / {form.model || 'any model'}. Enter serial(s) manually below.
                  </div>
                ) : (
                  <div
                    className="space-y-1 max-h-44 overflow-auto"
                    data-testid="quick-ha-picker-results"
                  >
                    {pickResults.models.map((m) => (
                      <div
                        key={`${m.product_id || 'unl'}::${m.brand}::${m.model}::${m.form_factor}`}
                        className="bg-white border border-slate-200 rounded p-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-800">
                            {m.brand} · {m.model}
                            {m.form_factor && <span className="text-slate-500"> · {m.form_factor}</span>}
                            {m.sale_unit && (
                              <span
                                className={`ml-1.5 inline-block text-[9px] font-bold uppercase tracking-wider rounded px-1 py-0.5 align-middle ${
                                  m.sale_unit === 'kit' || m.sale_unit === 'pair'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-slate-100 text-slate-600'
                                }`}
                                data-testid={`quick-ha-picker-saleunit-${m.product_id || 'unl'}`}
                                title={
                                  m.sale_unit === 'kit' || m.sale_unit === 'pair'
                                    ? 'Kit/Pair — one price covers both ears'
                                    : 'Single — each ear priced separately'
                                }
                              >
                                {m.sale_unit === 'kit' ? 'KIT' : m.sale_unit === 'pair' ? 'PAIR' : 'SINGLE'}
                              </span>
                            )}
                            {typeof m.mrp === 'number' && m.mrp > 0 && (
                              <span className="ml-1 text-[10px] text-slate-500">
                                · ₹{m.mrp.toLocaleString('en-IN')}
                              </span>
                            )}
                          </span>
                          <span className="text-[10px] text-slate-500">
                            {(m.units || []).length} in stock
                          </span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {(m.units || []).map((u) => {
                            const sn = (u.serial_no || '').toUpperCase();
                            const isLeft  = (form.serial_left  || '').toUpperCase() === sn;
                            const isRight = (form.serial_right || '').toUpperCase() === sn;
                            const isPicked = isLeft || isRight;
                            return (
                              <button
                                key={u.serial_id}
                                type="button"
                                onClick={() => pickInventoryUnit(u, m)}
                                data-testid={`quick-ha-picker-serial-${u.serial_id}`}
                                className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition ${
                                  isPicked
                                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                                    : 'bg-white border-slate-300 text-slate-700 hover:border-indigo-400 hover:bg-indigo-50'
                                }`}
                                title={
                                  isPicked
                                    ? `Assigned to ${isLeft ? 'Left' : 'Right'} — click to unpick`
                                    : `Assign to ${form.side === 'right' ? 'Right' : form.side === 'left' ? 'Left' : (form.serial_left ? 'Right' : 'Left')} ear`
                                }
                              >
                                {u.serial_no}
                                {isLeft && ' · L'}
                                {isRight && ' · R'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Side-aware serial fields */}
            <div className={`mt-3 grid gap-3 ${form.side === 'both' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {(form.side === 'both' || form.side === 'left') && (
                <SerialInputField
                  label={form.side === 'both' ? 'Left ear · Serial number' : 'Left ear · Serial number'}
                  value={form.serial_left}
                  onChange={u('serial_left')}
                  state={serialState.left}
                  testidPrefix="quick-ha-serial-left"
                  placeholder="PHO-RIC-2026LXX"
                  costValue={form.serial_left_cost}
                  onCostChange={u('serial_left_cost')}
                  costTestid="quick-ha-serial-left-cost"
                  grnValue={form.serial_left_grn}
                  onGrnChange={u('serial_left_grn')}
                  grnTestid="quick-ha-serial-left-grn"
                />
              )}
              {(form.side === 'both' || form.side === 'right') && (
                <SerialInputField
                  label={form.side === 'both' ? 'Right ear · Serial number' : 'Right ear · Serial number'}
                  value={form.serial_right}
                  onChange={u('serial_right')}
                  state={serialState.right}
                  testidPrefix="quick-ha-serial-right"
                  placeholder="PHO-RIC-2026RXX"
                  costValue={form.serial_right_cost}
                  onCostChange={u('serial_right_cost')}
                  costTestid="quick-ha-serial-right-cost"
                  grnValue={form.serial_right_grn}
                  onGrnChange={u('serial_right_grn')}
                  grnTestid="quick-ha-serial-right-grn"
                />
              )}
            </div>

            {/* Amber advisory panel — only rendered when at least one
                visible side is "not in inventory". Explains that the
                backend will materialise the serial + a `unlisted`
                catalogue SKU on the fly, and points staff at the
                cost-price input for margin reporting. */}
            {(() => {
              const sidesVisible = form.side === 'both' ? ['left', 'right'] : [form.side];
              const anyNotFound = sidesVisible.some((s) => serialState[s]?.status === 'not_found');
              if (!anyNotFound) return null;
              return (
                <div
                  className="mt-3 bg-amber-50 border border-amber-300 rounded p-3 text-[11px] leading-relaxed text-amber-900"
                  data-testid="quick-ha-external-serial-advisory"
                >
                  <div className="font-bold uppercase tracking-wider text-[10px] mb-1">
                    External-sourced serial · will be auto-created as inventory
                  </div>
                  One or more serials aren't in stock. When you submit, the sale
                  will create the physical unit as a <b>SOLD</b> inventory row
                  (state · SOLD, external-source ref · this sale) and, if this
                  brand + model isn't in your catalogue yet, will add an
                  <b> unlisted</b> SKU so future service tickets / AMC / warranty
                  can still reference it. Enter the <b>cost price</b> for
                  accurate margin reporting.
                </div>
              );
            })()}

            {/* Device spec — colour + power + wire/tube length. Fields
                shown depend on ha_type (RIC → receiver spec, BTE → power
                class + slim tube; custom shells → colour only). For
                Side = Both ears we render per-ear cards so left and
                right can carry different wires/powers. */}
            <div className="mt-3">
              <HASpecPicker
                deviceType={form.ha_type}
                side={form.side === 'both' ? 'BOTH' : (form.side === 'left' ? 'L' : 'R')}
                value={spec}
                onChange={setSpec}
                testIdPrefix="quick-ha-spec"
                title="Device specification"
              />
            </div>
          </section>

          {/* Warranty */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Warranty</h3>
            <div className="grid grid-cols-3 gap-3 items-end">
              <Field label="Manufacturer warranty (months)">
                <input type="number" min="0" max="240" value={form.warranty_months} onChange={u('warranty_months')}
                  data-testid="quick-ha-warranty-months"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
              </Field>
              <label className="flex items-center gap-2 text-sm pb-2 col-span-2 cursor-pointer">
                <input type="checkbox" checked={form.extended_warranty} onChange={ub('extended_warranty')}
                  data-testid="quick-ha-extended-warranty"
                  className="rounded text-indigo-600" />
                <span className="font-semibold text-slate-700">Extended warranty offered</span>
              </label>
              {form.extended_warranty && (
                <>
                  <Field label="Extended period (months)">
                    <input type="number" min="0" max="240" value={form.extended_warranty_months} onChange={u('extended_warranty_months')}
                      data-testid="quick-ha-ew-months"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
                  </Field>
                  <Field label="Source">
                    <select value={form.extended_warranty_source} onChange={u('extended_warranty_source')}
                      data-testid="quick-ha-ew-source"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                      <option value="manufacturer">Manufacturer / Company</option>
                      <option value="clinic">Clinic-offered</option>
                    </select>
                  </Field>
                </>
              )}
            </div>
          </section>

          {/* Pricing */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Pricing (₹)</h3>
            <div className="grid grid-cols-4 gap-3">
              <Field label="MRP" required>
                <input type="number" min="0" step="0.01" value={form.mrp} onChange={u('mrp')}
                  data-testid="quick-ha-mrp"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm tabular-nums" />
              </Field>
              <Field label="Sale price" required hint="What the patient pays (incl. GST)">
                <input type="number" min="0" step="0.01" value={form.sale_price} onChange={u('sale_price')}
                  data-testid="quick-ha-sale-price"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm tabular-nums" />
              </Field>
              <Field label="Discount" hint={discountAuto ? `Auto: ₹${discountAuto}` : undefined}>
                <input type="number" min="0" step="0.01" value={form.discount_amount}
                  onChange={u('discount_amount')}
                  data-testid="quick-ha-discount"
                  placeholder={discountAuto || '0'}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm tabular-nums" />
              </Field>
              <Field label="GST %">
                <input type="number" min="0" max="28" step="1" value={form.gst_rate} onChange={u('gst_rate')}
                  data-testid="quick-ha-gst"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm tabular-nums" />
              </Field>
            </div>
          </section>

          {/* Payment */}
          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Payment</h3>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Status">
                <select value={form.payment_status} onChange={u('payment_status')}
                  data-testid="quick-ha-pay-status"
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm">
                  <option value="fully_paid">Fully paid</option>
                  <option value="advance_paid">Advance paid</option>
                  <option value="unpaid">Unpaid (bill later)</option>
                </select>
              </Field>
              <Field label="Mode">
                <select value={form.payment_mode} onChange={u('payment_mode')}
                  data-testid="quick-ha-pay-mode"
                  disabled={form.payment_status === 'unpaid'}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm disabled:bg-slate-100">
                  {PAY_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Payment date">
                <input type="date" value={form.payment_date} onChange={u('payment_date')}
                  data-testid="quick-ha-pay-date"
                  disabled={form.payment_status === 'unpaid'}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm disabled:bg-slate-100" />
              </Field>
              {form.payment_status === 'advance_paid' && (
                <>
                  <Field label="Advance amount" required>
                    <input type="number" min="0" step="0.01" value={form.advance_amount} onChange={u('advance_amount')}
                      data-testid="quick-ha-advance"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm tabular-nums" />
                  </Field>
                  <Field label="Balance due (auto)">
                    <input value={`₹ ${balance.toLocaleString('en-IN')}`} readOnly
                      data-testid="quick-ha-balance"
                      className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm bg-slate-50 tabular-nums" />
                  </Field>
                  <Field label="Expected payment date">
                    <input type="date" value={form.expected_payment_date} onChange={u('expected_payment_date')}
                      data-testid="quick-ha-expected"
                      className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
                  </Field>
                </>
              )}
              {/* ── Cash-to-collect-now pill (2026-08-26) ────────────
                   Only rendered when Apply-Advance is enabled with a
                   positive amount AND status="Fully paid" — the exact
                   combination where the raw "Fully paid" would have
                   over-charged the patient. Read-only, so staff can't
                   accidentally overwrite the derived cash amount. */}
              {applyAdv?.enabled && Number(applyAdv?.amount || 0) > 0
                && form.payment_status === 'fully_paid' && (
                <Field label="Cash to collect now (auto)">
                  <input
                    readOnly
                    data-testid="quick-ha-cash-now"
                    value={`₹ ${Math.max(0, (parseFloat(form.sale_price || 0) - Number(applyAdv.amount || 0))).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`}
                    className="w-full border border-emerald-200 rounded px-2 py-1.5 text-sm bg-emerald-50 tabular-nums font-semibold text-emerald-800"
                  />
                </Field>
              )}
            </div>
          </section>

          {/* Notes */}
          <section>
            <Field label="Notes">
              <textarea rows={2} value={form.notes} onChange={u('notes')}
                data-testid="quick-ha-notes"
                placeholder="e.g. patient prefers right-ear program 2 for restaurants…"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
            </Field>
          </section>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 sticky bottom-0 bg-white">
            <button onClick={guardedClose} data-testid="quick-ha-cancel" className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded">
              Cancel
            </button>
            <button onClick={submit} disabled={saving}
              data-testid="quick-ha-submit"
              className="px-4 py-2 text-xs font-bold bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white rounded shadow-md">
              {saving ? 'Saving…' : 'Record Sale + Fit + Invoice'}
            </button>
          </div>
        </div>
      </div>
      {unsavedPill}
    </div>
  );
}


// ─── Serial input with live inventory badge ─────────────────────────

function SerialInputField({ label, value, onChange, state, testidPrefix, placeholder,
                           costValue, onCostChange, costTestid,
                           grnValue, onGrnChange, grnTestid }) {
  // Status pill colour + copy
  let pill = null;
  if (state?.status === 'available') {
    pill = (
      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-badge-available`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />In stock
      </span>
    );
  } else if (state?.status === 'conflict') {
    pill = (
      <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-badge-conflict`}
        title={state.reason || state.state}>
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />{state.state || 'Unavailable'}
      </span>
    );
  } else if (state?.status === 'not_found') {
    pill = (
      <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-badge-untracked`}
        title="Sale will auto-create this as external-sourced inventory">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Not in inventory
      </span>
    );
  } else if (state?.status === 'checking') {
    pill = (
      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-badge-checking`}>
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />Checking…
      </span>
    );
  }

  // Border colour mirrors the status pill
  const borderClass =
    state?.status === 'available' ? 'border-emerald-400 focus:border-emerald-500'
    : state?.status === 'conflict' ? 'border-rose-400 focus:border-rose-500'
    : state?.status === 'not_found' ? 'border-amber-400 focus:border-amber-500'
    : 'border-slate-300 focus:border-indigo-500';

  // Helper text under the input — explains the consequence of the current state
  let hint = null;
  if (state?.status === 'conflict') {
    hint = <span className="text-[10px] text-rose-700 mt-1 block">{state.reason || `Cannot sell — currently ${state.state}.`}</span>;
  } else if (state?.status === 'not_found' && value && value.length >= 3) {
    hint = <span className="text-[10px] text-amber-700 mt-1 block">Will be auto-created as external-sourced inventory (SOLD).</span>;
  } else if (state?.status === 'available') {
    hint = <span className="text-[10px] text-emerald-700 mt-1 block">Inventory will decrement by 1 when you submit.</span>;
  }

  return (
    <label className="block">
      <span className="flex items-center justify-between mb-1">
        <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {label} <span className="text-rose-500">*</span>
        </span>
        {pill}
      </span>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`${testidPrefix}-input`}
        className={`w-full border rounded px-2 py-1.5 text-sm font-mono uppercase focus:outline-none focus:ring-1 ${borderClass}`}
      />
      {hint}

      {/* Cost-price + GRN inputs — only visible when this serial is
          flagged "not in inventory". Both are nullable on submit if
          left blank. Rendered as a 2-column grid so cost and GRN sit
          side-by-side on the same row (per user request 2026-08-27). */}
      {state?.status === 'not_found' && value && value.length >= 3 && onCostChange && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
              Cost price <span className="text-slate-400 font-normal normal-case tracking-normal">· optional</span>
            </span>
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-slate-500 font-semibold">₹</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={costValue}
                onChange={onCostChange}
                placeholder="0.00"
                data-testid={costTestid}
                className="w-full border border-amber-300 focus:border-amber-500 rounded px-2 py-1 text-xs tabular-nums focus:outline-none focus:ring-1"
              />
            </div>
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">
              GRN number <span className="text-slate-400 font-normal normal-case tracking-normal">· optional</span>
            </span>
            <input
              type="text"
              value={grnValue || ''}
              onChange={onGrnChange}
              placeholder="e.g. GRN-2026-0087"
              data-testid={grnTestid}
              maxLength={80}
              className="w-full border border-amber-300 focus:border-amber-500 rounded px-2 py-1 text-xs font-mono uppercase focus:outline-none focus:ring-1"
            />
          </div>
        </div>
      )}
    </label>
  );
}

